import { PFSDQ_ACTIVITIES, type PFSDQMResultPayload } from '../tests/PFSDQMTest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const getPFSDQSeverityLabel = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  if (total <= 100) return 'Faixa baixa';
  if (total <= 200) return 'Faixa intermediária';
  return 'Faixa alta';
};

export const getPFSDQSeverityTone = (total?: number | null): string => {
  if (typeof total !== 'number') return 'bg-slate-100 text-slate-700';
  if (total <= 100) return 'bg-emerald-100 text-emerald-700';
  if (total <= 200) return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

export const getPFSDQScoreColor = (total?: number | null): string => {
  if (typeof total !== 'number') return '#475569';
  if (total <= 100) return '#10b981';
  if (total <= 200) return '#f59e0b';
  return '#f43f5e';
};

export const getPFSDQInterpretationText = (
  dyspneaScore?: number | null,
  fatigueScore?: number | null,
  changeScore?: number | null,
): string => {
  const domains = [
    { label: 'dispneia', value: dyspneaScore ?? -1 },
    { label: 'fadiga', value: fatigueScore ?? -1 },
    { label: 'mudança nas atividades', value: changeScore ?? -1 },
  ].sort((a, b) => b.value - a.value);

  if (domains.every((domain) => domain.value < 0)) {
    return 'Não há dados suficientes para interpretar o PFSDQ-M.';
  }

  const dominant = domains[0];
  return `O PFSDQ-M mostra maior impacto no domínio de ${dominant.label}. Pontuações mais altas indicam maior comprometimento funcional relacionado à dispneia, fadiga e mudança no padrão de atividades diárias.`;
};

const formatBooleanAnswer = (value: unknown) => {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  return '—';
};

const formatScaleAnswer = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? `${value}/10` : '—';

const formatCountAnswer = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric}` : '—';
};

const getTopActivities = (values: Array<number | null> | Array<{ value: number | null; never?: boolean }>) => {
  if (!Array.isArray(values)) return [];

  return values
    .map((entry, index) => {
      const rawValue =
        typeof entry === 'number'
          ? entry
          : entry && typeof entry === 'object' && !entry.never
            ? entry.value
            : null;

      return {
        label: PFSDQ_ACTIVITIES[index] || `Atividade ${index + 1}`,
        value: typeof rawValue === 'number' && Number.isFinite(rawValue) ? rawValue : null,
      };
    })
    .filter((item) => item.value != null)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    .slice(0, 3);
};

interface PFSDQMResultRendererProps {
  payload: Partial<PFSDQMResultPayload> | null | undefined;
}

function DomainCard({
  label,
  score,
  answered,
  tone,
  topActivities,
}: {
  label: string;
  score: number | null;
  answered: number;
  tone: string;
  topActivities: { label: string; value: number | null }[];
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">{label}</div>
          <div className="mt-1 text-xs opacity-80">Atividades respondidas: {answered}</div>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-black ${tone}`}>{score ?? '—'}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">/100</div>
        </div>
      </div>

      <div className="mt-4 space-y-2 text-xs">
        <div className="font-semibold opacity-80">Atividades com maior impacto</div>
        {topActivities.length > 0 ? (
          topActivities.map((activity) => (
            <div key={`${label}-${activity.label}`} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
              <span className="font-medium">{activity.label}</span>
              <span className="font-bold">{activity.value ?? '—'}/10</span>
            </div>
          ))
        ) : (
          <div className="rounded-lg bg-slate-50 px-3 py-2">Sem atividades pontuadas.</div>
        )}
      </div>
    </div>
  );
}

function GeneralBlock({
  label,
  general,
  tone,
}: {
  label: string;
  general: Record<string, unknown>;
  tone: string;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 text-slate-800 shadow-sm">
      <div className={`text-sm font-semibold ${tone}`}>{label}</div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg bg-slate-50 px-3 py-3">
          <div className="text-[10px] uppercase tracking-[0.15em] opacity-70">Já sentiu?</div>
          <div className="mt-1 font-semibold">{formatBooleanAnswer(general.q1)}</div>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-3">
          <div className="text-[10px] uppercase tracking-[0.15em] opacity-70">Vezes por mês</div>
          <div className="mt-1 font-semibold">{formatCountAnswer(general.q2_per_month)}</div>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-3">
          <div className="text-[10px] uppercase tracking-[0.15em] opacity-70">Ano passado</div>
          <div className="mt-1 font-semibold">{formatScaleAnswer(general.q3)}</div>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-3">
          <div className="text-[10px] uppercase tracking-[0.15em] opacity-70">Hoje</div>
          <div className="mt-1 font-semibold">{formatScaleAnswer(general.q4)}</div>
        </div>
        <div className="col-span-2 rounded-lg bg-slate-50 px-3 py-3">
          <div className="text-[10px] uppercase tracking-[0.15em] opacity-70">Maioria das AVDs</div>
          <div className="mt-1 font-semibold">{formatScaleAnswer(general.q5)}</div>
        </div>
      </div>
    </div>
  );
}

export default function PFSDQMResultRenderer({ payload }: PFSDQMResultRendererProps) {
  const dyspneaScore = typeof payload?.dyspnea?.score === 'number' ? payload.dyspnea.score : null;
  const fatigueScore = typeof payload?.fatigue?.score === 'number' ? payload.fatigue.score : null;
  const changeScore = typeof payload?.change?.score === 'number' ? payload.change.score : null;
  const total = typeof payload?.total === 'number' ? payload.total : null;
  const interpretation = resolveAssessmentInterpretationText(
    payload,
    getPFSDQInterpretationText(dyspneaScore, fatigueScore, changeScore),
  );

  const dyspneaAnswered = payload?.manualCounts?.dyspneaAnswered ?? (Array.isArray(payload?.dyspnea?.activities) ? payload.dyspnea.activities.filter((value) => value != null).length : 0);
  const fatigueAnswered = payload?.manualCounts?.fatigueAnswered ?? (Array.isArray(payload?.fatigue?.activities) ? payload.fatigue.activities.filter((value) => value != null).length : 0);
  const changeAnswered =
    payload?.manualCounts?.changeAnswered ??
    (Array.isArray(payload?.change?.activities) ? payload.change.activities.filter((entry) => !entry?.never && entry?.value != null).length : 0);

  const changeActivities = Array.isArray(payload?.change?.activities) ? payload.change.activities : [];
  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Pontuação PFSDQ-M"
        value={total ?? '—'}
        valueColor={getPFSDQScoreColor(total)}
        valueCaption={typeof total === 'number' ? `${total}/300` : 'Pontuação indisponível'}
        classification={getPFSDQSeverityLabel(total)}
        supportingText="Soma dos domínios de dispneia, fadiga e mudança nas atividades."
        interpretation={interpretation}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <GeneralBlock label="Questionário geral da dispneia" general={payload?.dyspnea?.general || {}} tone="text-rose-700" />
        <GeneralBlock label="Questionário geral da fadiga" general={payload?.fatigue?.general || {}} tone="text-orange-700" />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <DomainCard
          label="Dispneia"
          score={dyspneaScore}
          answered={dyspneaAnswered}
          tone="text-rose-600"
          topActivities={getTopActivities(payload?.dyspnea?.activities || [])}
        />
        <DomainCard
          label="Fadiga"
          score={fatigueScore}
          answered={fatigueAnswered}
          tone="text-orange-600"
          topActivities={getTopActivities(payload?.fatigue?.activities || [])}
        />
        <DomainCard
          label="Mudança"
          score={changeScore}
          answered={changeAnswered}
          tone="text-sky-700"
          topActivities={getTopActivities(changeActivities)}
        />
      </div>
    </div>
  );
}
